// Right pane of the Delivery view: a vertical list of Migration Unit cards.
// Each card shows pipeline status (process map generated?, specs generated?,
// dependsOn chips, feature count, total effort if any) and exposes
// "Open MU →" which (Phase 2+) drills into the MU's tabs.
//
// Drag a feature onto a card = move it into that MU.
// Drag the card header up/down = reorder MUs.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GripVertical, ChevronDown, ExternalLink, Pencil, Trash2, Plus, Sparkles } from 'lucide-react';
import type {
  DiscoveryFeature,
  DeliveryAnalysisPayload,
  DeliveryMutation,
  FeatureSpecsPayload,
} from '../hooks/sessionTypes';
import { UNASSIGNED_MU } from '../hooks/sessionTypes';
import { rollupMus, muColor, scoringFor, type MuRollup } from '../utils/deliveryAnalysisModel';
import '../styles/DeliveryPipeline.css';

interface Props {
  sessionId: string;
  features: DiscoveryFeature[];
  payload: DeliveryAnalysisPayload;
  specsPayload: FeatureSpecsPayload;
  selectedFeatureId: string | null;
  onSelect: (id: string | null) => void;
  onPatch: (m: DeliveryMutation) => Promise<void>;
  onRunAiGrouping: () => Promise<void>;
  grouping: boolean;
}

const DeliveryPipeline: React.FC<Props> = ({
  sessionId, features, payload, specsPayload, selectedFeatureId, onSelect, onPatch, onRunAiGrouping, grouping,
}) => {
  const rollups = rollupMus(features, payload);
  const [dragFeature, setDragFeature] = useState<string | null>(null);
  const [dragMu, setDragMu] = useState<string | null>(null);
  const [hoverMu, setHoverMu] = useState<string | null>(null);
  const [hoverHeader, setHoverHeader] = useState<string | null>(null);
  const [creatingMu, setCreatingMu] = useState(false);
  const [newMuName, setNewMuName] = useState('');

  const reorderTo = async (target: string, source: string) => {
    if (source === target) return;
    const names = payload.migrationUnits.map((m) => m.name);
    const sIdx = names.indexOf(source);
    const tIdx = names.indexOf(target);
    if (sIdx < 0 || tIdx < 0) return;
    names.splice(sIdx, 1);
    names.splice(tIdx, 0, source);
    await onPatch({ type: 'reorderMus', names });
  };

  const submitCreate = async () => {
    const trimmed = newMuName.trim().slice(0, 30);
    setCreatingMu(false);
    setNewMuName('');
    if (!trimmed) return;
    await onPatch({ type: 'createMu', name: trimmed });
  };

  return (
    <section className="dv-pipeline">
      <header className="dv-pipeline-header">
        <h2 className="dv-pipeline-title">Migration units</h2>
        <span className="dv-pipeline-count">{rollups.length}</span>
        <div className="dv-pipeline-actions">
          <button
            type="button"
            className="dv-btn dv-btn--ghost"
            onClick={onRunAiGrouping}
            disabled={grouping || features.length === 0}
            title="Re-run AI grouping over visible features"
          >
            {grouping ? <span className="dv-spinner" /> : <Sparkles size={13} />}
            {grouping ? 'Grouping…' : 'Re-group with AI'}
          </button>
          {!creatingMu ? (
            <button
              type="button"
              className="dv-btn dv-btn--primary"
              onClick={() => setCreatingMu(true)}
              title="Add a new migration unit"
            >
              <Plus size={13} /> New MU
            </button>
          ) : (
            <form
              className="dv-pipeline-create"
              onSubmit={(e) => { e.preventDefault(); submitCreate(); }}
            >
              <input
                autoFocus
                value={newMuName}
                onChange={(e) => setNewMuName(e.target.value)}
                onBlur={submitCreate}
                onKeyDown={(e) => { if (e.key === 'Escape') { setCreatingMu(false); setNewMuName(''); } }}
                placeholder="MU name…"
                maxLength={30}
              />
            </form>
          )}
        </div>
      </header>

      <div className="dv-pipeline-body">
        {rollups.length === 0 ? (
          <div className="dv-pipeline-empty">
            No migration units yet. Import a CSV or extract features from a Discovery session,
            then group them.
          </div>
        ) : (
          <ul className="dv-mu-list">
            {rollups.map((r) => (
              <MuCard
                key={r.mu.name}
                sessionId={sessionId}
                rollup={r}
                payload={payload}
                specsPayload={specsPayload}
                selectedFeatureId={selectedFeatureId}
                onSelect={onSelect}
                onPatch={onPatch}
                isDragSource={dragMu === r.mu.name}
                isDropTarget={hoverMu === r.mu.name}
                isHeaderDropTarget={hoverHeader === r.mu.name}
                onCardDragOver={(e) => { if (dragFeature) { e.preventDefault(); setHoverMu(r.mu.name); } }}
                onCardDragLeave={(e) => { if (e.currentTarget === e.target) setHoverMu(null); }}
                onCardDrop={async (e) => {
                  e.preventDefault();
                  setHoverMu(null);
                  if (dragFeature) {
                    await onPatch({ type: 'moveFeature', featureId: dragFeature, toMu: r.mu.name });
                    setDragFeature(null);
                  }
                }}
                onHeaderDragStart={(e) => {
                  if (r.mu.name === UNASSIGNED_MU) { e.preventDefault(); return; }
                  setDragMu(r.mu.name);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', r.mu.name);
                }}
                onHeaderDragEnd={() => setDragMu(null)}
                onHeaderDragOver={(e) => {
                  if (dragMu && dragMu !== r.mu.name) { e.preventDefault(); setHoverHeader(r.mu.name); }
                }}
                onHeaderDragLeave={(e) => { if (e.currentTarget === e.target) setHoverHeader(null); }}
                onHeaderDrop={async (e) => {
                  e.preventDefault();
                  setHoverHeader(null);
                  if (dragMu && dragMu !== r.mu.name) await reorderTo(r.mu.name, dragMu);
                  setDragMu(null);
                }}
                onFeatureDragStart={setDragFeature}
                onFeatureDragEnd={() => setDragFeature(null)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default DeliveryPipeline;

// ── Card ──────────────────────────────────────────────────────────

interface MuCardProps {
  sessionId: string;
  rollup: MuRollup;
  payload: DeliveryAnalysisPayload;
  specsPayload: FeatureSpecsPayload;
  selectedFeatureId: string | null;
  onSelect: (id: string | null) => void;
  onPatch: (m: DeliveryMutation) => Promise<void>;
  isDragSource: boolean;
  isDropTarget: boolean;
  isHeaderDropTarget: boolean;
  onCardDragOver: (e: React.DragEvent) => void;
  onCardDragLeave: (e: React.DragEvent) => void;
  onCardDrop: (e: React.DragEvent) => void;
  onHeaderDragStart: (e: React.DragEvent) => void;
  onHeaderDragEnd: () => void;
  onHeaderDragOver: (e: React.DragEvent) => void;
  onHeaderDragLeave: (e: React.DragEvent) => void;
  onHeaderDrop: (e: React.DragEvent) => void;
  onFeatureDragStart: (id: string) => void;
  onFeatureDragEnd: () => void;
}

const MuCard: React.FC<MuCardProps> = ({
  sessionId, rollup, payload, specsPayload, selectedFeatureId, onSelect, onPatch,
  isDragSource, isDropTarget, isHeaderDropTarget,
  onCardDragOver, onCardDragLeave, onCardDrop,
  onHeaderDragStart, onHeaderDragEnd, onHeaderDragOver, onHeaderDragLeave, onHeaderDrop,
  onFeatureDragStart, onFeatureDragEnd,
}) => {
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(rollup.mu.name);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState(rollup.mu.description);

  const isUnassigned = rollup.mu.name === UNASSIGNED_MU;
  const hasFlow = !!rollup.mu.flowText && rollup.mu.flowText.trim().length > 0;
  const specsDrafted = rollup.features.reduce(
    (n, f) => n + (specsPayload.specs[f.candidateId]?.markdown ? 1 : 0),
    0,
  );
  const specsFresh = rollup.features.reduce(
    (n, f) => {
      const s = specsPayload.specs[f.candidateId];
      if (!s?.markdown) return n;
      // Stale if the MU flow was regenerated after the spec was drafted.
      if (rollup.mu.flowGeneratedAt && s.generatedFromFlowAt && s.generatedFromFlowAt !== rollup.mu.flowGeneratedAt) return n;
      return n + 1;
    },
    0,
  );
  const allSpecsFresh = rollup.features.length > 0 && specsFresh === rollup.features.length;

  const submitRename = async () => {
    const next = renameValue.trim().slice(0, 30);
    setRenaming(false);
    if (!next || next === rollup.mu.name) return;
    await onPatch({ type: 'renameMu', from: rollup.mu.name, to: next });
  };

  const submitDescription = async () => {
    setEditingDescription(false);
    if (descriptionDraft === rollup.mu.description) return;
    await onPatch({ type: 'updateMuDescription', name: rollup.mu.name, description: descriptionDraft });
  };

  const deleteThisMu = async () => {
    if (!window.confirm(`Delete migration unit "${rollup.mu.name}"? Features in it will move to Unassigned.`)) return;
    await onPatch({ type: 'deleteMu', name: rollup.mu.name });
  };

  return (
    <li
      className={`dv-mu ${isDragSource ? 'dv-mu--dragging' : ''} ${isDropTarget ? 'dv-mu--drop' : ''}`}
      style={{ borderColor: rollup.color, backgroundColor: `${rollup.color}0a` }}
      onDragOver={onCardDragOver}
      onDragLeave={onCardDragLeave}
      onDrop={onCardDrop}
    >
      <header
        className={`dv-mu-head ${isHeaderDropTarget ? 'dv-mu-head--drop' : ''}`}
        draggable={!isUnassigned && !renaming}
        onDragStart={onHeaderDragStart}
        onDragEnd={onHeaderDragEnd}
        onDragOver={onHeaderDragOver}
        onDragLeave={onHeaderDragLeave}
        onDrop={onHeaderDrop}
      >
        <GripVertical size={11} className="dv-mu-grip" />
        <span className="dv-mu-color" style={{ background: rollup.color }} />
        {renaming ? (
          <input
            className="dv-mu-rename"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={submitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') setRenaming(false);
            }}
            autoFocus
            maxLength={30}
          />
        ) : (
          <button
            type="button"
            className="dv-mu-name"
            onClick={() => {
              if (isUnassigned) return;
              setRenameValue(rollup.mu.name);
              setRenaming(true);
            }}
            title={isUnassigned ? UNASSIGNED_MU : 'Click to rename'}
          >
            {rollup.mu.name}
          </button>
        )}
        <span className="dv-mu-meta">
          {rollup.visibleFeatures.length}f
          {rollup.totalEffort > 0 && ` · ${rollup.totalEffort} pts`}
          {rollup.hiddenCount > 0 && <span className="dv-mu-hidden"> · {rollup.hiddenCount} hidden</span>}
        </span>
        {!isUnassigned && (
          <button
            type="button"
            className="dv-mu-icon-btn"
            onClick={deleteThisMu}
            title="Delete migration unit"
            aria-label="Delete migration unit"
          >
            <Trash2 size={11} />
          </button>
        )}
        <Link
          to={`/sessions/${sessionId}/delivery/units/${encodeURIComponent(rollup.mu.name)}`}
          className="dv-mu-open"
          title="Open this migration unit in a new tab"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open <ExternalLink size={10} />
        </Link>
      </header>

      <div className="dv-mu-body">
        {editingDescription ? (
          <textarea
            className="dv-mu-desc-input"
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            onBlur={submitDescription}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setEditingDescription(false); setDescriptionDraft(rollup.mu.description); }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitDescription();
            }}
            autoFocus
            maxLength={240}
            placeholder="What ships in this MU and why…"
          />
        ) : (
          <button
            type="button"
            className="dv-mu-desc"
            onClick={() => { setDescriptionDraft(rollup.mu.description); setEditingDescription(true); }}
            title="Click to edit description"
          >
            {rollup.mu.description ? rollup.mu.description : <span className="dv-mu-desc-placeholder">+ description</span>}
            <Pencil size={9} className="dv-mu-desc-edit-hint" />
          </button>
        )}

        <ul className="dv-mu-status">
          <li className={hasFlow ? 'dv-mu-status-line dv-mu-status-line--ok' : 'dv-mu-status-line'}>
            <span className="dv-status-bullet">{hasFlow ? '●' : '○'}</span>
            <span>Process map {hasFlow ? 'generated' : 'not generated yet'}</span>
          </li>
          <li className={allSpecsFresh ? 'dv-mu-status-line dv-mu-status-line--ok' : 'dv-mu-status-line'}>
            <span className="dv-status-bullet">{allSpecsFresh ? '●' : (specsDrafted > 0 ? '◐' : '○')}</span>
            <span>
              Specs: {specsDrafted} of {rollup.features.length} drafted
              {specsDrafted > 0 && specsFresh < specsDrafted && (
                <span className="dv-mu-stale-tip"> · {specsDrafted - specsFresh} stale</span>
              )}
            </span>
          </li>
        </ul>

        {rollup.mu.dependsOn.length > 0 && (
          <div className="dv-mu-deps">
            <span className="dv-mu-deps-label">depends on</span>
            {rollup.mu.dependsOn.map((d) => (
              <span key={d} className="dv-mu-dep-chip" style={{ borderColor: muColor(d), color: muColor(d) }}>{d}</span>
            ))}
          </div>
        )}

        <ul className="dv-mu-features">
          {rollup.visibleFeatures.map((f) => (
            <MiniFeature
              key={f.candidateId}
              feature={f}
              payload={payload}
              selected={selectedFeatureId === f.candidateId}
              onSelect={() => onSelect(selectedFeatureId === f.candidateId ? null : f.candidateId)}
              onDragStart={() => onFeatureDragStart(f.candidateId)}
              onDragEnd={onFeatureDragEnd}
            />
          ))}
          {rollup.visibleFeatures.length === 0 && rollup.hiddenCount === 0 && (
            <li className="dv-mu-empty">Drop a feature here.</li>
          )}
        </ul>

        {rollup.hiddenCount > 0 && (
          <details className="dv-mu-hidden-fold">
            <summary><ChevronDown size={10} /> {rollup.hiddenCount} hidden</summary>
            <ul className="dv-mu-features dv-mu-features--hidden">
              {rollup.features.filter((f) => payload.hidden[f.candidateId]).map((f) => (
                <MiniFeature
                  key={f.candidateId}
                  feature={f}
                  payload={payload}
                  selected={selectedFeatureId === f.candidateId}
                  hidden
                  onSelect={() => onSelect(selectedFeatureId === f.candidateId ? null : f.candidateId)}
                />
              ))}
            </ul>
          </details>
        )}
      </div>
    </li>
  );
};

interface MiniFeatureProps {
  feature: DiscoveryFeature;
  payload: DeliveryAnalysisPayload;
  selected: boolean;
  hidden?: boolean;
  onSelect: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

const MiniFeature: React.FC<MiniFeatureProps> = ({
  feature, payload, selected, hidden, onSelect, onDragStart, onDragEnd,
}) => {
  const scoring = scoringFor(payload, feature.candidateId);
  return (
    <li
      className={`dv-mini ${selected ? 'dv-mini--selected' : ''} ${hidden ? 'dv-mini--hidden' : ''}`}
      draggable={!hidden && !!onDragStart}
      onDragStart={(e) => {
        if (!onDragStart) return;
        onDragStart();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', feature.candidateId);
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      title={feature.description || feature.name}
    >
      <span className="dv-mini-name">{feature.name}</span>
      <span className="dv-mini-chips">
        {scoring.effort != null && <span className="dv-mini-pts">{scoring.effort}</span>}
        {scoring.value && <span className={`dv-mini-val dv-mini-val--${scoring.value}`}>{scoring.value[0].toUpperCase()}</span>}
      </span>
    </li>
  );
};
